import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  KeyRound,
  Copy,
  Check,
  X,
  Folder,
  FileText,
  ChevronRight,
  RefreshCw,
  Mail,
} from 'lucide-react';
import type { ExportJob, GetExportCredentialsResponse, SftpEntry } from '@atlas/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { getExportCredentials, listExportContents } from '../lib/callables.js';
import { ExportProgress } from './ExportProgress.js';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function formatDate(ms: number | null): string {
  if (!ms) return '';
  return new Date(ms).toLocaleString();
}

function SftpBrowser({ exportId }: { exportId: string }) {
  const [path, setPath] = useState('/');
  const [entries, setEntries] = useState<SftpEntry[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (target: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await listExportContents({ exportId, path: target });
        setPath(result.data.path);
        setEntries(result.data.entries);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not connect over SFTP.');
      } finally {
        setLoading(false);
      }
    },
    [exportId],
  );

  useEffect(() => {
    void load('/');
  }, [load]);

  const segments = path.split('/').filter(Boolean);

  function crumbPath(index: number): string {
    return '/' + segments.slice(0, index + 1).join('/');
  }

  return (
    <div className="rounded border border-[#e9e9e7]">
      {/* Breadcrumb + refresh */}
      <div className="flex items-center justify-between gap-2 border-b border-[#e9e9e7] bg-[#fafafa] px-3 py-1.5">
        <div className="flex items-center gap-1 text-xs text-[#73726e] min-w-0 flex-wrap">
          <button
            type="button"
            onClick={() => void load('/')}
            className="hover:text-[#37352f] font-medium"
          >
            root
          </button>
          {segments.map((seg, i) => (
            <span key={i} className="flex items-center gap-1 min-w-0">
              <ChevronRight size={11} className="text-[#c4c2bd] shrink-0" />
              <button
                type="button"
                onClick={() => void load(crumbPath(i))}
                className="hover:text-[#37352f] truncate"
              >
                {seg}
              </button>
            </span>
          ))}
        </div>
        <button
          type="button"
          onClick={() => void load(path)}
          disabled={loading}
          className="text-[#aeaaa4] hover:text-[#37352f] disabled:opacity-50 shrink-0"
          title="Refresh"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && <p className="px-3 py-2 text-xs text-[#e03e3e]">{error}</p>}

      <div className="max-h-56 overflow-y-auto">
        {loading && !entries && (
          <p className="px-3 py-2 text-xs text-[#aeaaa4]">Connecting over SFTP…</p>
        )}
        {entries && entries.length === 0 && !loading && (
          <p className="px-3 py-2 text-xs text-[#aeaaa4]">This folder is empty.</p>
        )}
        {entries?.map((entry) => {
          const isDir = entry.type === 'directory';
          const childPath = `${path.replace(/\/$/, '')}/${entry.name}`;
          return (
            <div
              key={entry.name}
              className={`flex items-center justify-between gap-3 px-3 py-1.5 border-b border-[#f1f1ef] last:border-0 ${
                isDir ? 'cursor-pointer hover:bg-[#f7f7f5]' : ''
              }`}
              onClick={isDir ? () => void load(childPath) : undefined}
            >
              <div className="flex items-center gap-2 min-w-0">
                {isDir ? (
                  <Folder size={13} className="text-[#d9730d] shrink-0" />
                ) : (
                  <FileText size={13} className="text-[#aeaaa4] shrink-0" />
                )}
                <span className="text-xs text-[#37352f] truncate">{entry.name}</span>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[11px] text-[#aeaaa4] tabular-nums">
                  {formatDate(entry.modifiedAt)}
                </span>
                {!isDir && (
                  <span className="text-[11px] text-[#73726e] tabular-nums w-16 text-right">
                    {formatSize(entry.size)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="text-[#aeaaa4] hover:text-[#37352f] transition-colors"
      title="Copy"
    >
      {copied ? <Check size={13} className="text-[#0f7b6c]" /> : <Copy size={13} />}
    </button>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-[#f1f1ef] last:border-0">
      <span className="text-xs text-[#73726e]">{label}</span>
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs font-mono text-[#37352f] truncate">{value}</span>
        <CopyButton value={value} />
      </div>
    </div>
  );
}

/** Build a `mailto:` link handing the SFTP credentials to the client. */
function buildCredentialsMailto(
  companyName: string,
  creds: GetExportCredentialsResponse,
): string {
  const subject = `Your ${companyName} data export credentials`;
  const body = [
    `Hey ${companyName},`,
    '',
    'Here are the credentials for your export:',
    '',
    `Host: ${creds.endpoint}`,
    `Port: ${creds.port}`,
    `Username: ${creds.username}`,
    `Password: ${creds.password}`,
    `Home directory: ${creds.homeDirectory}`,
  ].join('\n');
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function useCredentials(exportId: string) {
  const [creds, setCreds] = useState<GetExportCredentialsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<GetExportCredentialsResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const result = await getExportCredentials({ exportId });
      setCreds(result.data);
      return result.data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load credentials.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [exportId]);

  return { creds, loading, error, load };
}

type CredentialsState = ReturnType<typeof useCredentials>;

/** Admin-only reveal of the SFTP password (host/port/user/home are already shown). */
function RevealPassword({ state }: { state: CredentialsState }) {
  const { creds, loading, error, load } = state;

  if (creds) {
    return (
      <div className="rounded border border-[#e9e9e7] bg-[#fafafa] px-3 py-2">
        <Field label="Password" value={creds.password} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => void load()}
        disabled={loading}
        className="flex items-center gap-1.5 self-start px-3 py-1.5 text-xs font-medium text-[#37352f] border border-[#e9e9e7] rounded hover:bg-[#f1f1ef] disabled:opacity-50 transition-colors"
      >
        <KeyRound size={13} />
        {loading ? 'Loading…' : 'Reveal SFTP password'}
      </button>
      {error && <p className="text-xs text-[#e03e3e]">{error}</p>}
    </div>
  );
}

function ToolbarTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${
        active
          ? 'border-[#37352f] text-[#37352f]'
          : 'border-transparent text-[#aeaaa4] hover:text-[#37352f]'
      }`}
    >
      {children}
    </button>
  );
}

export function ExportDetailModal({
  exp,
  onClose,
}: {
  exp: ExportJob & { id: string };
  onClose: () => void;
}) {
  const { userRecord } = useAuth();
  const isAdmin = Boolean(userRecord?.isAdmin);
  const hasSftp = Boolean(exp.sftp);

  const [tab, setTab] = useState<'report' | 'ftp'>('report');
  const credsState = useCredentials(exp.id);

  async function handleSendEmail() {
    const creds = credsState.creds ?? (await credsState.load());
    if (!creds) return;
    // Open in a new tab so the mail handler never navigates the app away.
    const a = document.createElement('a');
    a.href = buildCredentialsMailto(exp.companyName, creds);
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[560px] max-w-[95vw] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <h2 className="text-sm font-semibold text-[#37352f]">{exp.companyName}</h2>
            <p className="text-xs text-[#aeaaa4] font-mono mt-0.5">{exp.agencyId}</p>
          </div>
          <button onClick={onClose} className="text-[#aeaaa4] hover:text-[#37352f]">
            <X size={16} />
          </button>
        </div>

        {/* Toolbar: tabs on the left, actions on the right — always visible */}
        <div className="flex items-center justify-between gap-2 px-5 border-b border-[#e9e9e7]">
          <div className="flex items-center gap-1">
            <ToolbarTab active={tab === 'report'} onClick={() => setTab('report')}>
              Report
            </ToolbarTab>
            {hasSftp && (
              <ToolbarTab active={tab === 'ftp'} onClick={() => setTab('ftp')}>
                FTP preview
              </ToolbarTab>
            )}
          </div>

          {hasSftp && isAdmin && (
            <div className="flex items-center gap-1.5 py-1.5">
              <button
                type="button"
                onClick={() => void handleSendEmail()}
                disabled={credsState.loading}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-[#37352f] rounded hover:bg-[#2a2825] disabled:opacity-50 transition-colors"
              >
                <Mail size={13} />
                {credsState.loading ? 'Preparing…' : 'Send email'}
              </button>
            </div>
          )}
        </div>

        {credsState.error && (
          <p className="text-xs text-[#e03e3e] px-5 pt-2">{credsState.error}</p>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 pt-4 flex flex-col gap-4">
          {tab === 'ftp' && hasSftp ? (
            <SftpBrowser exportId={exp.id} />
          ) : (
            <>
              {exp.error && (
                <p className="text-xs text-[#e03e3e] bg-[#fbe8e8] border border-[#f3c9c9] rounded px-3 py-2">
                  {exp.error}
                </p>
              )}

              {/* Progress */}
              <div className="rounded border border-[#e9e9e7] px-3 py-2.5">
                <ExportProgress exp={exp} />
              </div>

              {/* Validation */}
              {exp.validation && (
                <div>
                  <h3 className="text-xs font-semibold text-[#37352f] mb-1.5">Validation</h3>
                  <div className="rounded border border-[#e9e9e7] px-3 py-2 text-xs text-[#73726e] flex flex-col gap-1">
                    <span>Tables exported: {exp.validation.tablesExported}</span>
                    <span>Total rows: {exp.validation.totalRows.toLocaleString()}</span>
                    <span
                      className={
                        exp.validation.isolationViolations > 0
                          ? 'text-[#e03e3e]'
                          : 'text-[#0f7b6c]'
                      }
                    >
                      Tenant-isolation violations: {exp.validation.isolationViolations}
                    </span>
                    <span
                      className={
                        exp.validation.countsReconciled ? 'text-[#0f7b6c]' : 'text-[#d9730d]'
                      }
                    >
                      Row counts reconciled: {exp.validation.countsReconciled ? 'yes' : 'no'}
                    </span>
                  </div>
                </div>
              )}

              {/* Attachments */}
              {exp.attachments && (
                <div>
                  <h3 className="text-xs font-semibold text-[#37352f] mb-1.5">Attachments</h3>
                  <div className="rounded border border-[#e9e9e7] px-3 py-2 text-xs flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[#0f7b6c]">Copied (new)</span>
                      <span className="font-mono tabular-nums text-[#37352f]">
                        {exp.attachments.transferred.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[#73726e]">Already present — skipped</span>
                      <span className="font-mono tabular-nums text-[#37352f]">
                        {exp.attachments.skipped.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span
                        className={exp.attachments.failed > 0 ? 'text-[#e03e3e]' : 'text-[#73726e]'}
                      >
                        Failed
                      </span>
                      <span
                        className={`font-mono tabular-nums ${
                          exp.attachments.failed > 0 ? 'text-[#e03e3e]' : 'text-[#37352f]'
                        }`}
                      >
                        {exp.attachments.failed.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t border-[#f1f1ef] pt-1.5 mt-0.5">
                      <span className="text-[#aeaaa4]">Total files</span>
                      <span className="font-mono tabular-nums text-[#73726e]">
                        {exp.attachments.totalFiles.toLocaleString()}
                      </span>
                    </div>
                    <p className="text-[11px] leading-snug text-[#aeaaa4] mt-0.5">
                      Files are copied source → export bucket. “Already present” files existed at
                      the destination from a prior run and were left untouched.
                    </p>
                  </div>
                </div>
              )}

              {/* Per-table results */}
              {exp.tableResults.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-[#37352f] mb-1.5">
                    Tables ({exp.tableResults.length})
                  </h3>
                  <div className="rounded border border-[#e9e9e7] max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[#fafafa]">
                        <tr className="border-b border-[#e9e9e7] text-[#aeaaa4]">
                          <th className="py-1.5 px-3 text-left font-medium">Table</th>
                          <th className="py-1.5 px-3 text-right font-medium">Rows</th>
                          <th className="py-1.5 px-3 text-right font-medium">Violations</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exp.tableResults.map((t) => (
                          <tr key={t.table} className="border-b border-[#f1f1ef] last:border-0">
                            <td className="py-1.5 px-3 font-mono text-[#37352f]">{t.table}</td>
                            <td className="py-1.5 px-3 text-right text-[#73726e] tabular-nums">
                              {t.exportedRowCount.toLocaleString()}
                            </td>
                            <td
                              className={`py-1.5 px-3 text-right tabular-nums ${
                                t.isolationViolations > 0 ? 'text-[#e03e3e]' : 'text-[#aeaaa4]'
                              }`}
                            >
                              {t.isolationViolations}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* SFTP access */}
              {exp.sftp && (
                <div>
                  <h3 className="text-xs font-semibold text-[#37352f] mb-1.5">SFTP access</h3>
                  <div className="rounded border border-[#e9e9e7] bg-[#fafafa] px-3 py-2 mb-2">
                    <Field label="Host" value={exp.sftp.endpoint} />
                    <Field label="Port" value={String(exp.sftp.port)} />
                    <Field label="Username" value={exp.sftp.username} />
                    <Field label="Home directory" value={exp.sftp.homeDirectory} />
                  </div>
                  {isAdmin ? (
                    <RevealPassword state={credsState} />
                  ) : (
                    <p className="text-xs text-[#aeaaa4]">
                      An admin can reveal the SFTP password.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

